/**
 * Today (the home tab; was "For You", renamed 2026-09-28 so it never collides with "Profile"): one vertical stream of cards, like a social feed, but FINITE and ordered by the moment map
 * (core forYouFeed, ROLE_ONTOLOGY), never by engagement. Each card has one primary action; check cards carry their
 * how-to. The stream ends with "You're all caught up". No infinite scroll, likes or streaks: the app is an instrument.
 */
import { PermitNudgeLine } from "../components/PermitNudge.tsx";
import { JustSetUpCard } from "../components/JustSetUp.tsx";
import { WantMoreLine } from "../components/WantMore.tsx";
import { FirstOpenCard } from "../components/ReviewSetupCard.tsx";
import { GotItAsk } from "../components/GotItAsk.tsx";
import { WaitingFixes } from "../components/WaitingFixes.tsx";
import { FoodRoundupCard } from "../components/FoodRoundup.tsx";
import { PathCard } from "../components/PathCard.tsx";
import { TodayRecordsSection } from "../components/TodayRecords.tsx";
import { Fragment, Suspense, lazy, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { placeState, checkinActionsFor, isFoodAtRiskFinding, ZERO_REPORT_WORDS, zeroReportSnoozed, ACTIVITY_KINDS, DELEGATE_WORDS, ORDER_STATUS, orderNeeds, FIRST_DAY_CHECKIN_ACTION, checkinAction, TODO_VIEW_WORDS, chooseNext, coversCard, problemsFromCards, WEEKLY_SUMMARY_ID, firstName, forYouFeed, greetingFor, getTemplate, checkAffordance, inputKindForExpected, nagLink, parseItemRef, type ActivityItem, type ActivityKind, type Employee, type FeedCard, type FeedRole, type HowTo, type NextInput } from "@logwave/core";
import type { TodayResponse } from "../api.ts";
import { HealthLine } from "../components/Health.tsx";
const HaveAskCard = lazyInline(() => import("../components/Have.tsx"), "HaveAskCard");
import { FindingBody } from "../components/Findings.tsx";
import { lazyInline } from "../lib/lazy.ts";
const EasyFixes = lazyInline(() => import("../components/Ask.tsx"), "EasyFixes");
const AskLoop = lazyInline(() => import("../components/AskFeedback.tsx"), "AskLoop");
import { Icon, type IconName } from "../components/Icons.tsx";
import { Mascot } from "../components/Brand.tsx";
import { HandoffShift, InCharge } from "../components/Handoff.tsx";
import { QuickLogCard } from "../components/QuickLog.tsx";
import { NextCard } from "../components/NextCard.tsx";
import { TodoDeck } from "../components/TodoDeck.tsx";
import { OrderAlertBody } from "../components/OrderAlert.tsx";
import { useTodoView } from "../lib/todoView.ts";
import { TipBody } from "../components/Tips.tsx";
import { Button, Chips, ErrorBox, LinkButton, Page, Pill, Skeleton, useToast } from "../components/ui.tsx";
import { checkActionLabel, initialsOf } from "../lib/labels.ts";
import { caughtEarly, controlledShifts, findingTitle } from "../lib/meaning.ts";
import { usePrefetchTabs } from "../lib/lazy.ts";
import { MOMENTS, MOMENT_LABEL, momentNow, type Moment } from "../lib/moment.ts";
import { isoToLocalTime, localDate, localTime } from "../lib/time.ts";
import { useLiveRefresh, useNow } from "../lib/useLive.ts";
import { useLoad } from "../lib/useLoad.ts";
import { useApi, useSession } from "../session.tsx";
import { savedOf } from "../lib/checkoff.ts";
import { useCheckoff } from "../components/Checkoff.tsx";
import { Badge, ItemCardDialog, taskKind, useTaskDone } from "../components/ItemCard.tsx";
import { SyncNote } from "./Today.tsx";
import { OPENING_TITLE, usePhaseBackfill } from "../lib/openingBackfill.ts";
const OpenBanner = lazyInline(() => import("./Opening.tsx"), "OpenBanner");
import { phaseOf } from "../lib/opening.ts";
import { Notice } from "../components/ui.tsx";
import { DemoAsk, DemoEnd } from "../components/DemoAsk.tsx";
import { isDemoSample, useDemoAskOpen } from "../lib/demoAsk.ts";
import { useOutbox } from "../lib/outbox.tsx";
import { CHECKIN_SENDING, queuedCheckin, withQueuedCheckin } from "../lib/checkinQueue.ts";
const SupplyLine = lazyInline(() => import("../components/StepSupply.tsx"), "SupplyLine");
import { ZeroReportsBody } from "../components/ZeroReports.tsx";
import { readZeroSnooze } from "../lib/zeroReports.ts";
/** "Use soon" (core foodscan.ts soonestToExpire): its own small chunk, loaded when there is something to show. */
const SoonestCard = lazy(() => import("../components/SoonestList.tsx").then((m) => ({ default: m.SoonestCard })));

const TABLES = ["log_entries", "daily_checkins", "incidents", "review_items", "employees", "tasks", "locations"] as const;

/** The weekly owner summary is fetched for owners (not admins or managers) on their location's Monday. */
export const weeklyDueFor = (role: string, date: string) => role === "owner" && new Date(`${date}T12:00:00Z`).getUTCDay() === 1;

export function feedRole(role: string): FeedRole {
  return role === "staff" ? "staff" : role === "manager" ? "manager" : "owner";
}

/** One source with the moment pill (core greetingFor). */
const greeting = (moment: Moment, hhmm: string) => greetingFor(moment, Number(hhmm.slice(0, 2)));

export function ForYou() {
  const api = useApi();
  const s = useSession();
  const demoChecksAsk = useDemoAskOpen("checks");
  const outbox = useOutbox(api);
  // The to-do view (core deck.ts): the list, or one task per card. Remembered per person. The deck's clock ticks every 30 s.
  const [todoView, setTodoView] = useTodoView(api);
  const tick = useNow(30_000);
  const [params] = useSearchParams();
  const isManager = s.can("manager");
  const isOwner = s.can("admin");
  const tz = s.location.timezone;
  const weeklyDue = weeklyDueFor(s.role, localDate(new Date(), tz));
  const data = useLoad(async () => {
    const date = localDate(new Date(), tz);
    const [today, entries, needs, checkins, people, tips, opening, routine] = await Promise.all([
      api.today(s.org.id, s.location.id),
      api.entries(s.org.id, { locationId: s.location.id, from: date, to: date }).catch(() => []),
      // What is on the supply list (managers): the feed shows one card that opens Order. Older servers: no card.
      isManager ? api.ordering(s.org.id, s.location.id).then((l) => orderNeeds(l)).catch(() => undefined) : Promise.resolve(undefined),
      isManager ? api.checkins(s.org.id, { locationId: s.location.id, from: date, to: date }).catch(() => []) : Promise.resolve([]),
      isManager ? api.employees(s.org.id).catch(() => []) : Promise.resolve([]),
      // My private tips (review.ts). Older servers don't have the route: the feed simply has no tip card.
      api.myTips(s.org.id).catch(() => ({ tips: [], thanks: [] })),
      // Owners and admins: "Can we open yet?" until it says yes (ONBOARDING step 4). Older servers: no card.
      isOwner ? api.opening(s.org.id, s.location.id).catch(() => null) : Promise.resolve(null),
      // The mixed routine (docs/ACTIVITIES.md). Older servers: no routine card.
      api.routine(s.org.id, s.location.id).then((r) => r.items).catch(() => null),
    ]);
    // The owner's evening line adds the close of day (core closeOfDay). Older servers: the line as before.
    const teamDay = isOwner && momentNow(s.location) === "evening" ? await api.daySummary(s.org.id, s.location.id).then((x) => x.line).catch(() => undefined) : undefined;
    const mine = tips.tips.filter((x) => x.locationId === s.location.id);
    // "Ask them to fix" notes go on top with a one-tap fix; the rest are ordinary tips.
    const fixAsks = mine.filter((x) => x.fix && !x.ackedAt).map((x) => ({ id: x.id, headline: x.headline, entryId: x.fix!.entryId }));
    return { today, entries, needs, checkins, people, tips: mine.filter((x) => !x.fix), fixAsks, teamDay, opening, routine };
  }, [api, s.org.id, s.location, isManager, isOwner]);
  // The weekly owner summary (core analytics.ts): owners, on Mondays, asked for only once the feed is up, so it never
  // holds the feed up. Older servers or any failure: no card.
  const feedUp = !!data.data;
  usePrefetchTabs(feedUp); // the tab screens load after Today is on screen, never before (lib/lazy.ts)
  const weekly = useLoad(async () => (weeklyDue && feedUp ? api.analytics(s.org.id, { locationId: s.location.id, weeks: 2 }).then((a) => a.weekly).catch(() => null) : null), [api, s.org.id, s.location.id, weeklyDue, feedUp]);
  const sync = useLiveRefresh(() => data.reload(), TABLES);
  usePhaseBackfill();

  const nowHHMM = localTime(new Date(), tz);
  const wanted = params.get("moment") as Moment | null;
  const moment: Moment = wanted && MOMENTS.includes(wanted) ? wanted : momentNow(s.location);
  const first = firstName(s.me.user.name);
  const d = data.data;
  const kicker = <>{d ? new Date(d.today.date + "T12:00:00Z").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" }) : "Today"} · {s.location.name}</>;
  const title = <span className="greet">{greeting(moment, nowHHMM)}, {first}</span>;

  if (!d) return <Page title={title} kicker={kicker} narrow>{data.error ? <ErrorBox error={`We couldn't load your feed. ${data.error}`} onRetry={data.reload} /> : <Skeleton rows={4} label="Loading Today" />}</Page>;

  // A check-in waiting on this device (offline, item 6) stands in for the server's until it sends; its band replaces
  // the check-in card (an all-clear may start; a not-OK stays blocked).
  const t = { ...d.today, me: withQueuedCheckin(d.today.me, queuedCheckin(outbox.pending, s.location.id, d.today.date), { orgId: s.org.id, locationId: s.location.id, employeeId: s.employeeId, date: d.today.date, state: placeState(s.location, s.org) }) };
  const verdicts = isManager ? controlledShifts(d.entries, d.checkins, d.people) : [];
  const notOk = verdicts.filter((v) => !v.controlled);
  const urgent = t.findings.find((f) => f.severity === "urgent");
  const allCards = forYouFeed({
    role: feedRole(s.role), moment, now: new Date().toISOString(), employeeId: s.employeeId, date: t.date,
    gate: s.employeeId ? t.me.gate : undefined, hasCheckin: !!t.me.checkin,
    openForFood: { ready: t.openForFood.ready, missing: t.openForFood.missing.map((m) => ({ key: m.key, label: m.label })) },
    expected: t.expected, findings: t.findings, reminders: t.me.reminders, orderNeeds: d.needs, integrity: t.integrity,
    // The owner's note about a quiet record (core zeroreports.ts), unless "Not now" is still in force in this browser.
    ...(t.zeroReports && !zeroReportSnoozed(readZeroSnooze(s.org.id, s.location.id), t.date) ? { zeroReports: t.zeroReports } : {}),
    caughtEarlyToday: caughtEarly(d.entries),
    controlled: { every: notOk.length === 0 && !urgent, missing: urgent ? findingTitle(urgent) : notOk[0]?.reason },
    tips: d.tips, fixAsks: d.fixAsks, ...(d.teamDay ? { teamDay: d.teamDay } : {}), notices: t.me.notices, handoffs: t.me.handoffs,
    // A shift lead in charge sees the open problems, not "all caught up" (audit #10).
    ...(t.me.isPersonInCharge ? { inCharge: true } : {}),
    ...(weekly.data && s.role === "owner" ? { weekly: weekly.data } : {}),
    // First-day mode (core firstDayMode): the day this location was set up, before and after its first record.
    startedOn: s.location.createdAt ? localDate(new Date(s.location.createdAt), tz) : undefined, recordsToday: d.entries.length,
    // Food to throw out now, for whoever is on shift (core feed.ts F1).
    foodToThrow: t.foodAtRisk ?? [],
  });
  const cards = t.me.sending ? allCards.filter((c) => c.kind !== "gate") : allCards;
  const onShift = t.onShift.map((o) => o.employee).filter(Boolean) as Employee[];
  // Staff get ONE Next card (core next.ts) and everything else folds under "Everything today" (fix T1).
  const staff = feedRole(s.role) === "staff";
  const stopAction = t.me.checkin?.health ? checkinAction(t.me.checkin.health) : undefined;
  const nextInput: NextInput | null = staff ? {
    now: new Date().toISOString(), moment, role: "staff", inCharge: !!t.me.isPersonInCharge,
    // Who is on today's shift list. Not a measured rush: there is no rush signal yet (next.ts `busy` seam).
    onShiftCount: t.onShift.length,
    gate: s.employeeId && !t.me.sending ? t.me.gate : undefined, hasCheckin: !!t.me.checkin,
    ...(t.me.checkin && !t.me.checkin.okToWork ? { stopWork: { line: stopAction ? checkinActionsFor(placeState(s.location, s.org))[stopAction].worker : "Your manager knows." } } : {}),
    expected: t.expected, problems: problemsFromCards(cards),
    // Delegation (core delegate.ts, next.ts N6): who asked, or whose it is, per check.
    assign: Object.fromEntries(Object.entries(t.assign ?? {}).map(([k, v]) => [k, { mine: v.mine, line: v.line }])),
    timeLabel: (iso) => isoToLocalTime(iso, tz), verb: (e) => checkActionLabel(e.templateId, e.label, getTemplate(e.templateId)?.title) || "Log it",
  } : null;
  const res = nextInput ? chooseNext(nextInput) : null;
  // The how-to for the Next check stays one tap away, under its button (never above it).
  const nextAction = res?.next.primary.action;
  const nextHowTo = nextAction?.kind === "check" ? t.expected.find((e) => e.key === nextAction.key)?.howTo : undefined;
  const listed = res ? cards.filter((c) => !coversCard(res.next, c)) : cards;
  // Owners and admins, until "Can we open yet?" says yes. First while planning or opening soon; else just before the end.
  // Where the place is: the location's answer, the same on every device (A8, migration 0065).
  const phase = phaseOf(s.location);
  const openingFirst = phase !== "open" && phase !== undefined;
  const openingCard = d.opening && !d.opening.ready ? (
    <li className="feed-item">
      <Shell icon="sign" kind="Open legally" tone="warn">
        <h2 className="fcard-title">{OPENING_TITLE}</h2>
        <OpenBanner gate={d.opening} compact phase={phase} />
        <LinkButton block icon="chevron" iconEnd to="/open">See what's left</LinkButton>
      </Shell>
    </li>
  ) : null;
    // One card for the whole routine, once the person may do food work; the list itself lives on Today.
  const routineCard = d.routine && d.routine.length > 0 && (!s.employeeId || t.me.gate.ready) ? <li className="feed-item"><RoutineCard items={d.routine} /></li> : null;

  return (
    <Page title={title} kicker={kicker} actions={<span className="row"><Pill tone="line" icon="clock">{MOMENT_LABEL[moment]}</Pill><SyncNote sync={sync} /></span>} narrow>
      {/* A check-in waiting to send: a status line, not a control, so it stays above the Next card. */}
      {t.me.sending && <Notice tone={t.me.checkin?.okToWork ? "info" : "warn"} icon="clock" title={CHECKIN_SENDING.title}>{t.me.checkin?.okToWork ? CHECKIN_SENDING.ok : CHECKIN_SENDING.notOk}</Notice>}
      <AskLoop />
      {/* "Did you get the …?" a day after a buy link, once (docs/APP-EVENTS.md). */}
      <GotItAsk />
      {res && (todoView === "cards" && nextInput
        ? <TodoDeck input={nextInput} now={new Date(tick).toISOString()} reload={data.reload} />
        : <NextCard res={res} reload={data.reload}>{nextHowTo && <HowToPeek howTo={nextHowTo} />}</NextCard>)}
      {/* Below the card, never above it: nothing tappable sits above the one primary button (core next.ts N4). */}
      {res && (
        <div className="todo-view">
          <Chips hideLabel label={TODO_VIEW_WORDS.label} options={[{ value: "list" as const, label: TODO_VIEW_WORDS.list }, { value: "cards" as const, label: TODO_VIEW_WORDS.cards }]} value={todoView} onChange={setTodoView} />
        </div>
      )}
      {/* Food at risk first (core feed.ts F1-F2). With a Next card it is already the card (next.ts N1b); without one (managers),
          it leads the page, above fixes already on their way. */}
      {!res && listed.some(isRiskCard) && <ol className="feed" aria-label="Food at risk">{listed.filter(isRiskCard).map((c) => <li key={c.id} className="feed-item"><Card c={c} t={t} onShift={onShift} reload={data.reload} cards={cards} /></li>)}</ol>}
      {/* A claimed prep's first open (core firstopen.ts): what we know and the plan's next step, then the day's cards. Below food at
          risk, above everything else; it is also "Review what Zak set up" (one card, never two). */}
      {isManager && <ol className="feed" aria-label="Your place"><FirstOpenCard /></ol>}
      {/* Fixes on their way (core whatidid.ts): "Waiting on the repair: walk-in" and what to do meanwhile, for everyone on shift. */}
      <WaitingFixes isPic={isManager || !!t.me.isPersonInCharge} />
      {/* "3 things to use or toss today" (core foodroundup.ts), for the people who chose the in-app roundup. */}
      <FoodRoundupCard scope={{ orgId: s.org.id, locationId: s.location.id }} />
      {/* Use soon (core foodlabel.ts soonestToExpire): the next few within 2 days, one tap to Threw it out · Opened it · Moved it. */}
      {!!t.foodSoonest?.length && <Suspense fallback={null}><SoonestCard items={t.foodSoonest} /></Suspense>}
      {/* "Add your food handler permit" until one is on file (core permitNudge): one quiet line, below the day's work, never a block. */}
      <PermitNudgeLine nudge={t.me.permitNudge} />
      {(() => {
        const items = (
          <>
        {/* Food at risk first (core feed.ts F1-F2): above the sample line, the check-in prompt and every routine card. */}
        {res && listed.filter(isRiskCard).map((c) => <li key={c.id} className="feed-item"><Card c={c} t={t} onShift={onShift} reload={data.reload} cards={cards} /></li>)}
        {/* The sample café (mock mode): one line to say so; the ask comes after the first check, never before (lib/demoAsk.ts). */}
        {isDemoSample(s.org) && (
          <li className="feed-item">
            {demoChecksAsk
              ? <DemoAsk feature="checks" />
              : <Notice tone="info" icon="spark" title="This is a sample café.">
                  <p>Tap anything. Nothing you do leaves this browser.</p>
                </Notice>}
          </li>
        )}
        {openingCard && openingFirst && openingCard}
        {/* Close, for the person in charge: the handoff is the one thing (moment map). */}
        {isManager && moment === "close" && (
          <li className="feed-item">
            <Shell icon="today" kind="Closing">
              <h2 className="fcard-title">Hand off the shift</h2>
              <p>The next person in charge gets what's still open on their list.</p>
              <HandoffShift t={t} onDone={data.reload} />
              <LinkButton block variant="secondary" icon="chevron" iconEnd to="/today?moment=close">See what's still open</LinkButton>
            </Shell>
          </li>
        )}
        {/* A staff member in charge (a shift lead): who's on shift, and at close a real hand-off (core handoff.ts). */}
        {!isManager && t.me.isPersonInCharge && (
          <li className="feed-item">
            <Shell icon="people" kind="You're in charge">
              <h2 className="fcard-title">{moment === "close" ? "Hand off the shift" : `On shift (${t.onShift.length})`}</h2>
              <InCharge t={t} closing={moment === "close"} onDone={data.reload} />
            </Shell>
          </li>
        )}
        {/* Delegation (core delegate.ts): a manager, or the person in charge, sees who has what and gives a task out. */}
        {t.canAssign && (
          <li className="feed-item">
            <Shell icon="people" kind="Tasks">
              <h2 className="fcard-title">{DELEGATE_WORDS.carrying}</h2>
              <p>See who has what, and give a task to someone.</p>
              <LinkButton block icon="chevron" iconEnd to="/carrying">{DELEGATE_WORDS.carrying}</LinkButton>
            </Shell>
          </li>
        )}
        <QuickLogCard ready={!s.employeeId || t.me.gate.ready} />
        {/* Location health for managers (docs/LOCATION_HEALTH.md): one line, one tap to every area. */}
        {isManager && <li className="feed-item"><HealthLine /></li>}
        {listed.filter((c) => !isRiskCard(c)).map((c) => (
          <Fragment key={c.id}>
            {c.kind === "caught_up" && routineCard}
            {c.kind === "caught_up" && openingCard && !openingFirst && openingCard}
            <li className="feed-item"><Card c={c} t={t} onShift={onShift} reload={data.reload} cards={cards} /></li>
            {/* "Want more?" after the day's checks are done, for a manager whose day has no checklist items (a new place starts lean), from the day after setup on (core feed.ts wantMoreOffer). */}
            {c.kind === "caught_up" && c.wantMore && isManager && <li className="feed-item"><WantMoreLine onDone={() => void data.reload()} /></li>}
          </Fragment>
        ))}
          </>
        );
        return res
          ? <details className="everything"><summary className="disclose">{res.more > 0 ? `Everything today (${res.more} more)` : "Everything today"}</summary><ol className="feed" aria-label="Everything today">{items}</ol></details>
          : <ol className="feed" aria-label="Today">{items}</ol>;
      })()}
      {/* The inviter's fruit: someone they added just joined (below what's due, never above a safety card). */}
      {isManager && <ol className="feed" aria-label="Your team"><JustSetUpCard /></ol>}
      {/* The owner's path (core journey.ts): the next 3 things, below what's due now (never above a safety card). */}
      {isManager && <PathCard />}
      {/* "Still have croissants? Where are they?" (core haveit.ts): what the place told us it has, asked once a day at most, low on the page,
          never while a safety gate is open and never in the rush (the day's service). */}
      <HaveAskCard scope={{ orgId: s.org.id, locationId: s.location.id }} gate={!!urgent || !t.openForFood.ready || (!!s.employeeId && (!t.me.gate.ready || !t.me.checkin))} quiet={moment === "day"} />
      {/* The trail (audit "Is it working?"): everything written down today, each opening into its record. */}
      <TodayRecordsSection />
      <DemoEnd />
    </Page>
  );
}

/** Food at risk (core feed.ts F1): a failed TCS reading or food to throw out. Shown first in the list. */
const isRiskCard = (c: FeedCard) => c.kind === "throw_out" || (c.kind === "finding" && isFoodAtRiskFinding(c.finding));

/** The post header: an icon "avatar", what kind of card it is, and when. */
/** "Today's routine: 3 to log · 5 to clean · 1 report": counts by kind, one tap to the list. */
function RoutineCard({ items }: { items: ActivityItem[] }) {
  const order: ActivityKind[] = ["report", "log", "clean", "maintain", "stock"];
  const parts = order.map((k) => [k, items.filter((i) => i.kind === k).length] as const).filter(([, n]) => n > 0);
  return (
    <Shell icon="do" kind="Routine">
      <h2 className="fcard-title">Today's routine</h2>
      <p>{parts.map(([k, n]) => `${ACTIVITY_KINDS[k].label} ${n}`).join(" · ")}</p>
      <LinkButton block icon="chevron" iconEnd to="/today">See my routine</LinkButton>
    </Shell>
  );
}

function Shell({ icon, kind, when, tone, children, className }: { icon: IconName; kind: ReactNode; when?: ReactNode; tone?: "stop" | "warn" | "go" | "info"; children: ReactNode; className?: string }) {
  return (
    <article className={`fcard${tone ? ` fcard-${tone}` : ""}${className ? ` ${className}` : ""}`}>
      <header className="fcard-head">
        <span className="fcard-av" aria-hidden="true"><Icon name={icon} size={20} /></span>
        <span className="fcard-kind">{kind}</span>
        {when && <span className="fcard-when">{when}</span>}
      </header>
      <div className="fcard-body">{children}</div>
    </article>
  );
}

function Card({ c, t, onShift, reload, cards }: { c: FeedCard; t: TodayResponse; onShift: Employee[]; reload: () => Promise<void>; cards: FeedCard[] }) {
  const s = useSession();
  const tz = s.location.timezone;
  switch (c.kind) {
    case "gate":
      return (
        <Shell icon="hands" kind="Before food work" tone="stop">
          <h2 className="fcard-title">{c.title}</h2>
          <p>{c.body}</p>
          <LinkButton big block to={c.action.to === "checkin" ? "/check-in" : "/learn"} icon={c.action.to === "checkin" ? "hands" : "learn"}>{c.action.label}</LinkButton>
        </Shell>
      );
    case "open_for_food": {
      const hasOwnCard = (key: string) => cards.some((x) => x.kind === "check" && x.check.key === key);
      const missing = t.expected.filter((e) => c.missing.some((m) => m.key === e.key));
      const next = missing[0];
      return (
        <Shell icon={c.ready ? "check" : c.firstDay ? "sun" : "x"} kind="Open for food" tone={c.ready ? "go" : c.firstDay ? "info" : "stop"}>
          <h2 className="fcard-title">{c.ready ? "Yes, you're open for food" : c.firstDay ? "Your first opening checks" : "Not yet"}</h2>
          {c.ready ? <p>All opening checks are done.</p> : (
            <>
              <p>{missing.length === 1 ? "One opening check left:" : `${missing.length} opening checks left:`}</p>
              <ul className="fcard-list">{missing.map((e) => <li key={e.key}><Link to={`/do?check=${encodeURIComponent(e.key)}`}>{e.label}</Link> <span className="caption">due {isoToLocalTime(e.dueAt, tz)}</span></li>)}</ul>
              {next && !hasOwnCard(next.key) && <LinkButton block icon="chevron" iconEnd to={`/do?check=${encodeURIComponent(next.key)}`}>{checkActionLabel(next.templateId, next.label, getTemplate(next.templateId)?.title)}</LinkButton>}
            </>
          )}
        </Shell>
      );
    }
    case "check": return <CheckCard c={c} reload={reload} />;
    case "first_check":
      return (
        <Shell icon="thermo" kind="Your first day" tone="info">
          <h2 className="fcard-title">{c.title}</h2>
          <p>{c.body}</p>
          {c.checkinFirst
            ? <LinkButton big block icon="hands" to="/check-in">{FIRST_DAY_CHECKIN_ACTION}</LinkButton>
            : <LinkButton big block icon="chevron" iconEnd to={`/do?check=${encodeURIComponent(c.check.key)}&guided=1&from=first`}>Start step 1</LinkButton>}
        </Shell>
      );
    case "first_record":
      return (
        <Shell icon="prove" kind="Your first day" tone="go">
          <h2 className="fcard-title">{c.title}</h2>
          <p>{c.body}</p>
          <LinkButton block icon="chevron" iconEnd to="/prove">See your binder</LinkButton>
        </Shell>
      );
    case "reminder":
      return <Shell icon="bell" kind={c.from} tone="info"><p className="fcard-lead">{c.body}</p></Shell>;
    case "throw_out":
      // Food past its date or its time (core feed.ts F1): whoever is on shift, one tap to the discard log.
      return (
        <Shell icon="trash" kind="Food at risk" tone="stop" className="fcard-finding">
          <h2 className="fcard-title">Throw out {c.food.name.toLowerCase()}</h2>
          <p>{c.food.reason} Throw it out and log it.</p>
          <LinkButton big block icon="trash" to={`/food?discard=${encodeURIComponent(c.food.id)}`}>Throw it out</LinkButton>
          <p className="caption">{c.food.citations.join(" · ")}</p>
        </Shell>
      );
    case "finding":
      return (
        <Shell icon="alert" kind={isFoodAtRiskFinding(c.finding) ? "Food at risk" : c.finding.severity === "urgent" ? "Do now" : c.finding.severity === "high" ? "Today" : "When you can"} tone={c.finding.severity === "urgent" ? "stop" : c.finding.severity === "high" ? "warn" : "info"} className="fcard-finding">
          <FindingBody f={c.finding} onShift={onShift} />
        </Shell>
      );
    case "order":
      return (
        <Shell icon="cart" kind="Supply list" tone="info">
          <p className="fcard-lead"><strong>{ORDER_STATUS.list}:</strong> {c.needs.names.slice(0, 4).join(", ")}{c.needs.count > 4 ? ` and ${c.needs.count - 4} more` : ""}.</p>
          <LinkButton block icon="chevron" iconEnd to="/order">Send the list</LinkButton>
          <p className="caption">You send it to your own supplier. We never order for you.</p>
        </Shell>
      );
    case "worth_a_look":
      return (
        <Shell icon="eye" kind="Worth a look">
          <p className="fcard-lead">{c.flag.message}</p>
          <p className="caption">A question, not an accusation. Real readings wobble a little; the same number every day is worth checking.</p>
        </Shell>
      );
    case "zero_reports":
      return (
        <Shell icon="eye" kind={ZERO_REPORT_WORDS.kicker}>
          <ZeroReportsBody note={c.note} today={t.date} onDone={() => void reload()} />
        </Shell>
      );
    case "caught_early":
      return (
        <Shell icon="check" kind="Caught early" tone="go">
          <h2 className="fcard-title">{c.count === 1 ? "One problem caught and fixed today" : `${c.count} problems caught and fixed today`}</h2>
          <p>A bad reading with a fix on record is the system working. Thank you for writing down the truth.</p>
        </Shell>
      );
    case "summary":
      if (c.id === WEEKLY_SUMMARY_ID) return <Shell icon="spark" kind="Last week"><h2 className="fcard-title">{c.title}</h2><p>{c.body}</p><LinkButton small variant="quiet" to="/insights">See the patterns</LinkButton></Shell>;
      return <Shell icon="moon" kind="Tonight" tone={c.title.includes("every") ? "go" : "warn"}><h2 className="fcard-title star-line">{c.title}</h2><p>{c.body}</p></Shell>;
    case "refresher":
      return (
        <Shell icon="book" kind="Today's reminder">
          <h2 className="fcard-title">{c.title}</h2>
          <p>{c.text}</p>
          <LinkButton small variant="quiet" to={`/learn/lesson/${c.lessonId}`}>Read the whole lesson</LinkButton>
        </Shell>
      );
    case "tip":
      return (
        <Shell icon="spark" kind="Tip for you" when={<Pill tone="line" icon="eye">Only you see this</Pill>} className="fcard-tip">
          <TipBody tip={c.tip} onAck={() => void reload()} />
        </Shell>
      );
    case "fix_ask":
      return (
        <Shell icon="write" kind="Please look again" when={<Pill tone="line" icon="eye">Only you see this</Pill>} tone="warn">
          <p className="fcard-lead">{c.headline}</p>
          <LinkButton big icon="write" to={`/do/fix/${encodeURIComponent(c.entryId)}?ask=${encodeURIComponent(c.id.replace(/^fix-/, ""))}`}>Write down the fix</LinkButton>
        </Shell>
      );
    case "thanks":
      return (
        <Shell icon="check" kind="Thank you" tone="go">
          <p className="fcard-lead">{c.body}</p>
        </Shell>
      );
    case "handoff":
      // An order-list alert (core orderalert.ts): who said what is low, one tap to thank them, one tap to the list.
      if (c.notice.kind === "order") {
        return (
          <Shell icon="cart" kind="Order list" tone="info" when={isoToLocalTime(c.notice.createdAt, tz)}>
            <OrderAlertBody notice={c.notice} onChanged={reload} />
          </Shell>
        );
      }
      // A task given to me (core delegate.ts), or a step-in covering one of mine. Like a nag: a button straight to the task.
      if (c.notice.kind === "assign") {
        const parsed = c.notice.refId && parseItemRef(c.notice.refId);
        return (
          <Shell icon="handoff" kind={parsed ? "Asked of you" : "Covered"} tone={parsed ? "warn" : "go"} when={isoToLocalTime(c.notice.createdAt, tz)}>
            <h2 className="fcard-title">{c.notice.title}</h2>
            {!parsed && <p>{c.notice.body}</p>}
            <LinkButton block icon="chevron" iconEnd to={parsed ? nagLink(c.notice.refId!, "web", "") : "/today"}>{parsed ? "Do this task now" : "See my list"}</LinkButton>
          </Shell>
        );
      }
      // A nag (core nag.ts): what to do, and a button straight to it. The notice body is the text the manager sent.
      if (c.notice.kind === "nag") {
        const to = c.notice.refId && parseItemRef(c.notice.refId) ? nagLink(c.notice.refId, "web", "") : "/do";
        return (
          <Shell icon="handoff" kind="Asked of you" tone="warn" when={isoToLocalTime(c.notice.createdAt, tz)}>
            <h2 className="fcard-title">{c.notice.title}</h2>
            <LinkButton block icon="chevron" iconEnd to={to}>Do this task now</LinkButton>
          </Shell>
        );
      }
      // A health alert (core locationhealth.ts healthAlerts): a red item left unfixed past its window. Staff open Today.
      if (c.notice.kind === "health") {
        return (
          <Shell icon="alert" kind="Health alert" tone="stop" when={isoToLocalTime(c.notice.createdAt, tz)}>
            <h2 className="fcard-title">{c.notice.title}</h2>
            <p>{c.notice.body}</p>
            <LinkButton block icon="chevron" iconEnd to={s.can("manager") ? "/health" : "/today"}>{s.can("manager") ? "See the health report" : "See today's list"}</LinkButton>
          </Shell>
        );
      }
      // Handed to me (core handoff.ts). No push yet: this card is where it arrives. A guest's private note (refId "guest:<id>:<area>", core guestnote.ts; the id is matched as [\w-] only, so it needs no escaping) rides
      // the same card: fixed words, never the note; one tap opens it in Guest notes.
      const gid = /^guest:([\w-]+)/.exec(String(c.notice.refId))?.[1], shift = c.notice.kind === "shift";
      return (
        <Shell icon="handoff" kind={gid ? "Guest note" : shift ? "Shift handed to you" : "Handed to you"} tone="warn" when={isoToLocalTime(c.notice.createdAt, tz)}>
          <h2 className="fcard-title">{c.notice.title}</h2>
          <p>{c.notice.body}</p>
          <LinkButton block icon="chevron" iconEnd to={gid ? `/guest-notes?note=${gid}` : shift ? "/today?moment=close" : "/today"}>{gid ? "Read the note" : shift ? "See what's still open" : "See the open reports"}</LinkButton>
        </Shell>
      );
    case "caught_up":
      return (
        <div className="fcard fcard-end" role="status">
          <Mascot wink />
          <h2 className="fcard-title">{c.title}</h2>
          <p>{c.body}</p>
        </div>
      );
  }
}

function CheckCard({ c, reload }: { c: Extract<FeedCard, { kind: "check" }>; reload: () => Promise<void> }) {
  const api = useApi();
  const s = useSession();
  const toast = useToast();
  const checkoff = useCheckoff();
  const [busy, setBusy] = useState(false);
  const e = c.check;
  const tz = s.location.timezone;
  const isTask = e.templateId === "task_done" && !!e.taskId;
  // A one-tap task ticks here; one that needs a reading, an answer or a photo opens its card, and its name always does (core checkinput.ts).
  const tap = isTask && taskKind(e.label) === "tap";
  const badge = checkAffordance(inputKindForExpected(e)).badge;
  const [cardOpen, setCardOpen] = useState(false);
  const verb = checkActionLabel(e.templateId, e.label, getTemplate(e.templateId)?.title);
  const to = `/do?check=${encodeURIComponent(e.key)}`;
  const doneTask = useTaskDone();
  async function markDone(extra: { note?: string | null; photoBase64?: string } = {}) {
    setBusy(true);
    try {
      if (await doneTask.complete({ label: e.label, taskId: e.taskId! }, extra, { onUndone: () => void reload() })) { setCardOpen(false); await reload(); }
    } finally { setBusy(false); }
  }
  return (
    <Shell icon={e.templateId === "sanitizer" ? "flask" : e.templateId === "restroom_check" ? "restroom" : tap ? "do" : "thermo"} kind="Due now"
      when={c.late ? <Pill tone="warn" icon="clock">Was due {isoToLocalTime(e.dueAt, tz)}</Pill> : <Pill tone="line" icon="clock">{isoToLocalTime(e.dueAt, tz)}</Pill>}>
      {isTask ? <h2 className="fcard-title"><button type="button" className="linklike" data-testid="fcard-name" onClick={() => setCardOpen(true)}>{e.label}</button></h2> : <h2 className="fcard-title">{e.label}</h2>}
      {badge && <p><Badge text={badge} /></p>}
      {e.hint && <p className="fcard-hint">{e.hint}</p>}
      {c.howTo && <HowToPeek howTo={c.howTo} />}
      <EasyFixes text={e.label} templateId={e.templateId} />
      <div className="btn-row">
        {tap ? <Button icon="check" busy={busy} onClick={() => void markDone()}>{verb}</Button> : isTask ? <Button icon="chevron" iconEnd onClick={() => setCardOpen(true)}>{verb}</Button> : <LinkButton icon="chevron" iconEnd to={to}>{verb}</LinkButton>}
        {c.howTo && !isTask && <LinkButton variant="secondary" small to={`${to}&guided=1`}>Walk me through it</LinkButton>}
      </div>
      {cardOpen && <ItemCardDialog item={{ label: e.label, taskId: e.taskId!, ...(e.hint ? { how: e.hint } : {}) }} busy={busy} onClose={() => setCardOpen(false)} onDone={(extra) => markDone(extra)} />}
    </Shell>
  );
}

/** "Show me how" folded into a check card: what you need and the steps, a tap away. */
function HowToPeek({ howTo }: { howTo: HowTo }) {
  // The supply chips (where it's kept; Not here? Find it on Amazon) draw once the person opens it: the card stays one primary button.
  const [open, setOpen] = useState(false);
  return (
    <details className="peek" onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary className="disclose small">Show me how ({howTo.need.length} {howTo.need.length === 1 ? "thing" : "things"}, {howTo.steps.length} {howTo.steps.length === 1 ? "step" : "steps"})</summary>
      <div className="peek-body">
        <p className="small"><strong>Why:</strong> {howTo.why}</p>
        <p className="howto-h">What you need</p>
        <ul className="peek-need">{howTo.need.map((n) => <li key={n}>{n}</li>)}</ul>
        <p className="howto-h">How</p>
        <ol className="peek-steps">{howTo.steps.map((st) => <li key={st}>{st}{open && <SupplyLine text={st} />}</li>)}</ol>
        <p className="caption">{howTo.citations.join(" · ")}</p>
      </div>
    </details>
  );
}
